import React, { useState, useEffect, useCallback, useRef } from 'react';
import { signedDocumentsApi, contractsApi } from '../services/api';
import { SignedDocument, Contract } from '../types';
import { useAuth } from '../context/AuthContext';
import {
  Upload,
  FileText,
  Trash2,
  Eye,
  Download,
  Printer,
  CheckCircle,
  AlertCircle,
  QrCode,
  Link2,
  Camera,
  X,
  Search,
  RefreshCw
} from 'lucide-react';

export default function SignedDocuments() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<SignedDocument[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filter, setFilter] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ filename: string; status: 'pending' | 'uploading' | 'success' | 'error'; message?: string }[]>([]);
  const [showAssignModal, setShowAssignModal] = useState<SignedDocument | null>(null);
  const [previewDocument, setPreviewDocument] = useState<SignedDocument | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [docsRes, contractsRes] = await Promise.all([
        signedDocumentsApi.getAll(),
        contractsApi.getAll()
      ]);
      setDocuments(docsRes.documents);
      setContracts(contractsRes.contracts);
    } catch (err: any) {
      setError('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf');
    if (files.length > 0) {
      await uploadFiles(files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await uploadFiles(Array.from(files));
    }
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const uploadFiles = async (files: File[]) => {
    setError('');
    setSuccessMessage('');
    
    const progress = files.map(f => ({ filename: f.name, status: 'pending' as const }));
    setUploadProgress(progress);

    let successCount = 0;
    let matchedCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress(prev => prev.map((p, idx) => 
        idx === i ? { ...p, status: 'uploading' } : p
      ));

      try {
        const result = await signedDocumentsApi.uploadWithScan(file);
        
        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { 
            ...p, 
            status: 'success',
            message: result.matchedByQr 
              ? `Per QR-Code zugeordnet` 
              : (result.contractId ? 'Zugeordnet' : 'Ohne Zuordnung')
          } : p
        ));
        
        successCount++;
        if (result.matchedByQr) matchedCount++;
      } catch (err: any) {
        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { ...p, status: 'error', message: err.response?.data?.error || 'Fehler' } : p
        ));
      }
    }

    if (successCount > 0) {
      setSuccessMessage(
        `${successCount} Dokument(e) hochgeladen` + 
        (matchedCount > 0 ? `, ${matchedCount} per QR-Code zugeordnet` : '')
      );
      await loadData();
    }

    // Progress nach 5 Sekunden ausblenden
    setTimeout(() => setUploadProgress([]), 5000);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Dokument wirklich löschen?')) return;
    
    try {
      await signedDocumentsApi.delete(id);
      setDocuments(documents.filter(d => d.id !== id));
      setSuccessMessage('Dokument gelöscht');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  const handleAssign = async (documentId: string, contractId: string | null) => {
    try {
      await signedDocumentsApi.assign(documentId, contractId);
      await loadData();
      setShowAssignModal(null);
      setSuccessMessage(contractId ? 'Dokument zugeordnet' : 'Zuordnung entfernt');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Zuordnung fehlgeschlagen');
    }
  };

  const handlePrint = (doc: SignedDocument) => {
    const printUrl = signedDocumentsApi.getViewUrl(doc.id);
    const printWindow = window.open(printUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.contract?.title?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filter === 'all' ||
      (filter === 'assigned' && doc.contractId) ||
      (filter === 'unassigned' && !doc.contractId);
    
    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Signierte Dokumente</h1>
          <p className="mt-1 text-sm text-gray-500">
            {documents.length} Dokument(e), {documents.filter(d => d.contractId).length} zugeordnet
          </p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
          >
            <Upload className="h-4 w-4 mr-2" />
            Dokument hochladen
          </button>
          <button
            onClick={() => cameraInputRef.current?.click()}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
          >
            <Camera className="h-4 w-4 mr-2" />
            Scannen
          </button>
        </div>
      </div>

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="application/pdf,image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          {successMessage}
          <button onClick={() => setSuccessMessage('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Upload-Fortschritt</h3>
          <div className="space-y-2">
            {uploadProgress.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm">
                {item.status === 'pending' && <div className="w-4 h-4 rounded-full bg-gray-200" />}
                {item.status === 'uploading' && <div className="w-4 h-4 rounded-full border-2 border-primary-600 border-t-transparent animate-spin" />}
                {item.status === 'success' && <CheckCircle className="h-4 w-4 text-green-500" />}
                {item.status === 'error' && <AlertCircle className="h-4 w-4 text-red-500" />}
                <span className="flex-1 truncate">{item.filename}</span>
                {item.message && (
                  <span className={`text-xs ${item.status === 'error' ? 'text-red-500' : 'text-gray-500'}`}>
                    {item.message}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Drag & Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`mb-6 border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          isDragging
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <Upload className={`h-12 w-12 mx-auto mb-4 ${isDragging ? 'text-primary-500' : 'text-gray-400'}`} />
        <p className="text-lg font-medium text-gray-900 mb-1">
          PDF-Dateien hier ablegen
        </p>
        <p className="text-sm text-gray-500 mb-4">
          oder klicken Sie auf die Buttons oben zum Auswählen
        </p>
        <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <QrCode className="h-4 w-4" />
            QR-Code wird automatisch erkannt
          </div>
        </div>
      </div>

      {/* Filter & Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">Alle Dokumente</option>
            <option value="assigned">Zugeordnet</option>
            <option value="unassigned">Nicht zugeordnet</option>
          </select>
          <button
            onClick={loadData}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="Aktualisieren"
          >
            <RefreshCw className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Documents List */}
      {filteredDocuments.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Dokumente gefunden</h3>
          <p className="text-gray-500">
            Laden Sie signierte Dokumente per Drag & Drop oder über die Buttons hoch.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dokument</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vertrag</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hochgeladen</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredDocuments.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-red-500" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                          {doc.originalName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatFileSize(doc.fileSize)} · {doc.pageCount} Seite(n)
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {doc.contract ? (
                      <div className="flex items-center gap-2">
                        <div className="text-sm text-gray-900">{doc.contract.title}</div>
                        {doc.matchedByQr && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                            <QrCode className="h-3 w-3 mr-1" />
                            QR
                          </span>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowAssignModal(doc)}
                        className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                      >
                        <Link2 className="h-4 w-4 mr-1" />
                        Zuordnen
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      doc.sourceType === 'FTP' ? 'bg-blue-100 text-blue-700' :
                      doc.sourceType === 'SCAN' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {doc.sourceType}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(doc.createdAt).toLocaleDateString('de-AT')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setPreviewDocument(doc)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                        title="Anzeigen"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <a
                        href={signedDocumentsApi.getDownloadUrl(doc.id)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                        title="Herunterladen"
                      >
                        <Download className="h-4 w-4" />
                      </a>
                      <button
                        onClick={() => handlePrint(doc)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                        title="Drucken"
                      >
                        <Printer className="h-4 w-4" />
                      </button>
                      {doc.contract && (
                        <button
                          onClick={() => setShowAssignModal(doc)}
                          className="p-1.5 text-gray-400 hover:text-amber-600 rounded"
                          title="Zuordnung ändern"
                        >
                          <Link2 className="h-4 w-4" />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                          title="Löschen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <AssignModal
          document={showAssignModal}
          contracts={contracts}
          onClose={() => setShowAssignModal(null)}
          onAssign={handleAssign}
        />
      )}

      {/* Preview Modal */}
      {previewDocument && (
        <PreviewModal
          document={previewDocument}
          onClose={() => setPreviewDocument(null)}
          onPrint={handlePrint}
        />
      )}
    </div>
  );
}

// Assign Modal Component
function AssignModal({
  document,
  contracts,
  onClose,
  onAssign
}: {
  document: SignedDocument;
  contracts: Contract[];
  onClose: () => void;
  onAssign: (documentId: string, contractId: string | null) => void;
}) {
  const [selectedContract, setSelectedContract] = useState<string>(document.contractId || '');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredContracts = contracts.filter(c =>
    c.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Vertrag zuordnen</h2>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">{document.originalName}</p>
        </div>

        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Vertrag suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        <div className="max-h-64 overflow-y-auto p-4">
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer border border-gray-200">
              <input
                type="radio"
                name="contract"
                value=""
                checked={selectedContract === ''}
                onChange={(e) => setSelectedContract(e.target.value)}
                className="w-4 h-4 text-primary-600"
              />
              <span className="text-sm text-gray-500 italic">Keine Zuordnung</span>
            </label>
            
            {filteredContracts.map((contract) => (
              <label
                key={contract.id}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border ${
                  selectedContract === contract.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="contract"
                  value={contract.id}
                  checked={selectedContract === contract.id}
                  onChange={(e) => setSelectedContract(e.target.value)}
                  className="w-4 h-4 text-primary-600"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">{contract.title}</div>
                  <div className="text-xs text-gray-500">
                    {contract.status} · {new Date(contract.createdAt).toLocaleDateString('de-AT')}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Abbrechen
          </button>
          <button
            onClick={() => onAssign(document.id, selectedContract || null)}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            Zuordnen
          </button>
        </div>
      </div>
    </div>
  );
}

// Preview Modal Component
function PreviewModal({
  document,
  onClose,
  onPrint
}: {
  document: SignedDocument;
  onClose: () => void;
  onPrint: (doc: SignedDocument) => void;
}) {
  const viewUrl = signedDocumentsApi.getViewUrl(document.id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-[95vw] h-[95vh] max-w-6xl flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{document.originalName}</h2>
            {document.contract && (
              <p className="text-sm text-gray-500">Zugeordnet: {document.contract.title}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <a
              href={signedDocumentsApi.getDownloadUrl(document.id)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </a>
            <button
              onClick={() => onPrint(document)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Printer className="h-4 w-4 mr-1" />
              Drucken
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        <div className="flex-1 bg-gray-100">
          <iframe
            src={viewUrl}
            className="w-full h-full border-0"
            title="PDF Vorschau"
          />
        </div>
      </div>
    </div>
  );
}
